import type {
	MembershipListResponse,
	ProjectDTO,
	TicketKind,
	TopicDTO,
} from "@zplanem/responses"
import { TICKET_KINDS } from "@zplanem/responses"
import type { Translations } from "@zplanem/translations"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import {
	useCreateTopic,
	useDeleteTopic,
	useUpdateTopic,
} from "../../hooks/use-topics"
import {
	clearedTicketBoardFilters,
	hasTicketBoardFilters,
	type TicketBoardFilters,
	topicsVisibleForTicketScope,
} from "./ticket-filter-model"
import { buildAssigneeOptions } from "./ticket-form-model"

type TicketCopy = Translations["tickets"]

const CONTROL_FOCUS =
	"focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

export function TicketFilterBar({
	propertyId,
	filters,
	projects,
	memberships,
	topics,
	canManageTopics,
	t,
	onChange,
	lockedProjectId,
}: {
	propertyId: string
	filters: TicketBoardFilters
	projects: ProjectDTO[]
	memberships: MembershipListResponse | undefined
	topics: TopicDTO[]
	canManageTopics: boolean
	t: TicketCopy
	onChange: (filters: TicketBoardFilters) => void
	lockedProjectId?: string
}) {
	const activeProjectId = lockedProjectId ?? filters.projectId ?? null
	const scopedTopics = topicsVisibleForTicketScope(topics, activeProjectId)

	return (
		<div className="mb-5 rounded-lg border border-border bg-card px-4 py-3">
			<div className="flex flex-wrap items-end gap-3">
				{lockedProjectId ? null : (
					<FilterSelect
						id="project"
						label={t.filterProject}
						value={filters.projectId ?? ""}
						onChange={(projectId) => {
							const nextProjectId = projectId || undefined
							const nextTopics = topicsVisibleForTicketScope(
								topics,
								nextProjectId ?? null,
							)
							const nextTopicId = nextTopics.some(
								(topic) => topic.id === filters.topicId,
							)
								? filters.topicId
								: undefined
							onChange({
								...filters,
								projectId: nextProjectId,
								topicId: nextTopicId,
							})
						}}
						options={[
							{ value: "", label: t.filterAllProjects },
							...projects.map((project) => ({
								value: project.id,
								label: project.name,
							})),
						]}
					/>
				)}
				<FilterSelect
					id="assignee"
					label={t.filterAssignee}
					value={filters.assigneeId ?? ""}
					onChange={(assigneeId) =>
						onChange({ ...filters, assigneeId: assigneeId || undefined })
					}
					options={[
						{ value: "", label: t.filterAllAssignees },
						...buildAssigneeOptions(memberships, t.unassigned).filter(
							(option) => option.value,
						),
					]}
				/>
				<FilterSelect
					id="topic"
					label={t.filterTopic}
					value={filters.topicId ?? ""}
					onChange={(topicId) =>
						onChange({ ...filters, topicId: topicId || undefined })
					}
					options={[
						{ value: "", label: t.filterAllTopics },
						...scopedTopics.map((topic) => ({
							value: topic.id,
							label: topic.name,
						})),
					]}
				/>
				<FilterSelect
					id="kind"
					label={t.filterKind}
					value={filters.kind ?? ""}
					onChange={(kind) =>
						onChange({
							...filters,
							kind: (kind || undefined) as TicketKind | undefined,
						})
					}
					options={[
						{ value: "", label: t.filterAllKinds },
						...TICKET_KINDS.map((kind) => ({
							value: kind,
							label: t.kind[kind],
						})),
					]}
				/>
				{hasTicketBoardFilters(filters, lockedProjectId) ? (
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => onChange(clearedTicketBoardFilters(lockedProjectId))}
					>
						{t.filterClear}
					</Button>
				) : null}
				{canManageTopics ? (
					<TopicManager
						propertyId={propertyId}
						t={t}
						topics={topics}
						projects={projects}
					/>
				) : null}
			</div>
		</div>
	)
}

function FilterSelect({
	id,
	label,
	value,
	options,
	onChange,
}: {
	id: string
	label: string
	value: string
	options: { value: string; label: string }[]
	onChange: (value: string) => void
}) {
	const selectId = `ticket-filter-${id}`
	return (
		<label className="min-w-36 flex-1 text-xs font-medium text-muted-foreground sm:flex-none">
			<span className="mb-1 block">{label}</span>
			<select
				id={selectId}
				aria-label={label}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className={`h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none ${CONTROL_FOCUS}`}
			>
				{options.map((option) => (
					<option key={option.value || "__all"} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</label>
	)
}

function TopicManager({
	propertyId,
	t,
	topics,
	projects,
}: {
	propertyId: string
	t: TicketCopy
	topics: TopicDTO[]
	projects: ProjectDTO[]
}) {
	const [open, setOpen] = useState(false)
	const [name, setName] = useState("")
	const [projectId, setProjectId] = useState("")
	const [editingId, setEditingId] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)
	const createTopic = useCreateTopic(propertyId)
	const updateTopic = useUpdateTopic(propertyId)
	const deleteTopic = useDeleteTopic(propertyId)

	async function saveTopic() {
		const trimmed = name.trim()
		if (!trimmed) return
		setError(null)
		try {
			if (editingId) {
				await updateTopic.mutateAsync({
					topicId: editingId,
					body: { name: trimmed, projectId: projectId || null },
				})
			} else {
				await createTopic.mutateAsync({
					name: trimmed,
					projectId: projectId || null,
				})
			}
			setName("")
			setProjectId("")
			setEditingId(null)
		} catch (err) {
			setError(
				err instanceof ApiError
					? err.message
					: editingId
						? t.topicUpdateError
						: t.topicCreateError,
			)
		}
	}

	return (
		<div className="relative">
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={() => setOpen((v) => !v)}
			>
				{t.topicManage}
			</Button>
			{open ? (
				<div className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-xl">
					<label className="block text-xs font-medium text-muted-foreground">
						<span className="mb-1 block">{t.topicNameLabel}</span>
						<input
							type="text"
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder={t.topicNamePlaceholder}
							className={`h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none placeholder:text-muted-foreground ${CONTROL_FOCUS}`}
						/>
					</label>
					<FilterSelect
						id="topic-scope"
						label={t.topicScopeLabel}
						value={projectId}
						onChange={setProjectId}
						options={[
							{ value: "", label: t.topicScopeProperty },
							...projects.map((project) => ({
								value: project.id,
								label: project.name,
							})),
						]}
					/>
					<Button
						type="button"
						variant="brand"
						size="sm"
						className="mt-2"
						disabled={
							!name.trim() || createTopic.isPending || updateTopic.isPending
						}
						onClick={() => {
							void saveTopic()
						}}
					>
						{editingId ? t.topicRename : t.topicCreate}
					</Button>
					<div className="mt-3 space-y-2">
						{topics.map((topic) => (
							<div
								key={topic.id}
								data-testid="ticket-topic-row"
								className="flex items-center justify-between gap-2"
							>
								<span className="truncate text-sm text-foreground">
									{topic.name}
								</span>
								<div className="flex shrink-0 gap-1">
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => {
											setEditingId(topic.id)
											setName(topic.name)
											setProjectId(topic.projectId ?? "")
										}}
									>
										{t.topicRename}
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={deleteTopic.isPending}
										onClick={async () => {
											if (!window.confirm(t.topicDeleteConfirm)) return
											setError(null)
											try {
												await deleteTopic.mutateAsync(topic.id)
											} catch (err) {
												setError(
													err instanceof ApiError
														? err.message
														: t.topicDeleteError,
												)
											}
										}}
									>
										{t.topicDelete}
									</Button>
								</div>
							</div>
						))}
					</div>
					{error ? (
						<div role="alert" className="mt-2 text-xs text-destructive">
							{error}
						</div>
					) : null}
				</div>
			) : null}
		</div>
	)
}
